/**
 * Company announcements — dashboard strip + admin compose/delete.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { CompanyAnnouncement, Paginated } from '@shared/types';
import { companyAnnouncementCreateInput } from '@shared/schemas/index.js';
import { z } from 'zod';

export type AnnouncementCreateBody = z.input<typeof companyAnnouncementCreateInput>;

export const announcementKeys = {
  all: ['announcements'] as const,
  list: () => [...announcementKeys.all, 'list'] as const,
};

export function useAnnouncements(limit = 8) {
  return useQuery({
    queryKey: announcementKeys.list(),
    queryFn: () =>
      api.get<Paginated<CompanyAnnouncement>>(`/api/announcements${qs({ limit })}`),
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AnnouncementCreateBody) =>
      api.post<CompanyAnnouncement>('/api/announcements', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/announcements/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}
